/* eslint-disable no-console */
// @refresh reset
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { mimir } from '@/services/api';
import { PageViewGTM } from '@lib/gtm';
import { TimeLine } from '@/types/types';
import { getCurrentHub } from '@sentry/nextjs';
import dynamic from 'next/dynamic';
import { listingRearranger } from '@/components/Ads/Rearrangers';
import ItemListNews from '../ItemListLastNews';
import getConfig from 'next/config';
import { sendPageViewEventToFacebookAPI } from '@/services/fbConvetionAPI';

const { publicRuntimeConfig } = getConfig();

const LoadingLogo = dynamic(() => import('../LoadingLogo'));
export const routeTypes = ['mais', 'categoria', 'editoria', 'tipo', 'tipoDePublicacao', 'produto', 'empresa', 'autor', 'celebridade', 'ultimas', 'id'] as const;

interface PageNews {
  posts?: TimeLine[];
  route: string;
  routeType: (typeof routeTypes)[number];
  paginacao?: string;
  rootPage?: string | string[];
  initialPage?: string | string[];
  noPagination?: boolean;
  noScroll?: boolean;
  backgroundColor?: string;
  itemsPerPage?: number;
  hideAds?: boolean;
  setNoItemsFound: Dispatch<SetStateAction<boolean>>;
}

interface ApiProps {
  status: number;
  nome: string;
  data: {
    timeline: {
      itens: TimeLine[];
      paginacao?: string;
    };
  };
}

export default function PageNewsWithMimirAndButtom({
  posts = [],
  paginacao,
  rootPage,
  initialPage,
  routeType,
  route,
  noPagination = false,
  noScroll = false,
  backgroundColor = 'mglGray50',
  itemsPerPage = 6,
  hideAds = false,
  setNoItemsFound,
}: PageNews) {
  const endpoints = (route: string) => {
    return {
      ultimas: `${publicRuntimeConfig.NEXT_PUBLIC_API_MIMIR}/timelines/ultimas/`,
      mais: `${publicRuntimeConfig.NEXT_PUBLIC_API_MIMIR}/timelines/mais/${route}/${route === 'lidas' ? '1-dia' : '7-dias'}`,
      categoria: `${publicRuntimeConfig.NEXT_PUBLIC_API_MIMIR}/timelines/ultimas/categoria/${route}`,
      editoria: `${publicRuntimeConfig.NEXT_PUBLIC_API_MIMIR}/timelines/ultimas/editoria/${route}`,
      tipo: `${publicRuntimeConfig.NEXT_PUBLIC_API_MIMIR}/timelines/ultimas/tipo/${route}`,
      tipoDePublicacao: `${publicRuntimeConfig.NEXT_PUBLIC_API_MIMIR}/timelines/ultimas/tipo/artigo/${route}`,
      empresa: `${publicRuntimeConfig.NEXT_PUBLIC_API_MIMIR}/timelines/ultimas/empresa/${route}`,
      produto: `${publicRuntimeConfig.NEXT_PUBLIC_API_MIMIR}/timelines/ultimas/${route}`,
      autor: `${publicRuntimeConfig.NEXT_PUBLIC_API_MIMIR}/timelines/ultimas/autor/${route}`,
      celebridade: `${publicRuntimeConfig.NEXT_PUBLIC_API_MIMIR}/timelines/ultimas/celebridade/${route}`,
      id: `${publicRuntimeConfig.NEXT_PUBLIC_API_MIMIR}/timelines/ultimas/id/${route}`,
    };
  };

  const currentSpan = getCurrentHub().getScope()?.getSpan();
  const pageNewsSpan = currentSpan && currentSpan.startChild({ op: 'pageNews' });

  const iniPage = initialPage ? parseInt(initialPage.toString().replace(`p`, ``), 10) : 0;

  const stepSize = Math.max(itemsPerPage, 1);
  const normalizedInitialPosts = Array.isArray(posts) ? posts : [];
  const initialVisibleCount = normalizedInitialPosts.length > 0 ? Math.min(stepSize, normalizedInitialPosts.length) : 0;

  const [atualPage, setAtualPage] = useState(iniPage || 0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLastPage, setIsLastPage] = useState(!paginacao);

  const [allPosts, setAllPosts] = useState<TimeLine[]>(normalizedInitialPosts);
  const [visibleCount, setVisibleCount] = useState(initialVisibleCount);
  const [dataPosts, setDataPosts] = useState(
    listingRearranger<TimeLine>({
      content: normalizedInitialPosts.slice(0, initialVisibleCount),
      primary: true,
      hasNoAds: hideAds,
      sizeDesktop: stepSize,
    })
  );

  const pageRef = useRef(iniPage && iniPage > 1 ? iniPage : 1);
  const paginationCodeRef = useRef(paginacao);
  const routeRef = useRef(route);
  const routeTypeRef = useRef(routeType);

  useEffect(() => {
    paginationCodeRef.current = paginacao;
    pageRef.current = iniPage && iniPage > 1 ? iniPage : 1;
    routeRef.current = route;
    routeTypeRef.current = routeType;

    const postsNormalized = Array.isArray(posts) ? posts : [];
    const nextVisible = postsNormalized.length > 0 ? Math.min(stepSize, postsNormalized.length) : 0;

    setAllPosts(postsNormalized.slice());
    setVisibleCount(nextVisible);
    setIsLastPage(!paginacao);
    setIsLoading(false);
    setNoItemsFound(postsNormalized.length === 0);
  }, [route, routeType, paginacao, iniPage, posts, stepSize, setNoItemsFound]);

  useEffect(() => {
    const totalPosts = allPosts.length;

    if (totalPosts === 0 && visibleCount !== 0) {
      setVisibleCount(0);
      return;
    }

    if (totalPosts > 0 && visibleCount === 0) {
      setVisibleCount(Math.min(stepSize, totalPosts));
      return;
    }

    if (visibleCount > totalPosts) {
      setVisibleCount(totalPosts);
    }
  }, [allPosts, visibleCount, stepSize]);

  useEffect(() => {
    const visiblePosts = allPosts.slice(0, visibleCount > 0 ? visibleCount : allPosts.length);

    const rearranged = listingRearranger<TimeLine>({
      content: visiblePosts,
      primary: true,
      hasNoAds: hideAds,
      sizeDesktop: stepSize,
    });

    setDataPosts(rearranged);
  }, [allPosts, visibleCount, hideAds, stepSize]);

  const getMorePosts = async () => {
    setIsLoading(true);
    const isFirstPage = !paginationCodeRef.current;

    try {
      const atualRoute = routeRef.current;
      const atualRouteType = routeTypeRef.current;

      const endpointsAtual = endpoints(atualRoute);

      const endpointUrl = new URL(endpointsAtual[atualRouteType]);
      if (paginationCodeRef.current) {
        endpointUrl.searchParams.delete('p');
        endpointUrl.searchParams.append('pagination', paginationCodeRef.current);
      }

      const { data } = await mimir.get<ApiProps>(endpointUrl.toString());
      const newItems = data.data.timeline.itens ?? [];

      paginationCodeRef.current = data.data.timeline.paginacao || undefined;

      setAllPosts(oldVal => [...oldVal, ...newItems]);

      if (newItems.length > 0) {
        pageRef.current = pageRef.current + 1;
      }

      setNoItemsFound(isFirstPage && newItems.length === 0);
      setIsLastPage(!data.data.timeline.paginacao);

      return newItems.length;
    } catch (error) {
      console.log(error);
      return 0;
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (isLoading) return;

    const totalPosts = allPosts.length;

    if (visibleCount < totalPosts) {
      setVisibleCount(prev => Math.min(prev + stepSize, totalPosts));
      return;
    }

    if (isLastPage) return;

    const loaded = await getMorePosts();
    if (loaded > 0) {
      setVisibleCount(prev => prev + stepSize);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const pagesArticles = Array.from(document.getElementsByClassName('pg-article'));
      let pg = parseInt(pagesArticles[0]?.getAttribute('data-page'), 10);

      if (pagesArticles[0]?.getBoundingClientRect()?.y > 0 && atualPage > 0) {
        setAtualPage(0);

        const rtPage = rootPage === '/' ? '/' : `${`/${rootPage}/`}${iniPage > 1 ? `p/${iniPage}/` : ''}`;
        if (!noPagination) window.history.pushState('', '', rtPage);
        PageViewGTM(window.location.pathname);
        sendPageViewEventToFacebookAPI({ eventSourceUrl: window.location.href, eventId: crypto.randomUUID() });

        return;
      }

      if (pagesArticles[0]?.getBoundingClientRect()?.y > 0) {
        return;
      }

      pagesArticles?.forEach((p, i) => {
        const next = i + 1 <= pagesArticles.length ? pagesArticles[i + 1] : undefined;
        const pNext = next ? next?.getBoundingClientRect() : undefined;
        const pAtual = p?.getBoundingClientRect();

        pg = parseInt(p?.getAttribute('data-page'), 10);

        if ((pAtual.y < 0 && pNext === undefined) || (pAtual.y < 0 && pNext.y > 0)) {
          if (pg !== atualPage) {
            let routeValue;
            if (rootPage === '/') {
              routeValue = pg === 1 ? `/ultimas/` : `/ultimas/p/${pg}/`;
            } else if (rootPage === 'podcast') {
              routeValue = pg === 1 ? `/podcast/ultimos/` : `/podcast/ultimos/p/${pg}/`;
            } else {
              routeValue = pg === 1 ? `/${rootPage}/` : `/${rootPage}/p/${pg}/`;
            }

            PageViewGTM(window.location.pathname);
            sendPageViewEventToFacebookAPI({ eventSourceUrl: window.location.href, eventId: crypto.randomUUID() });
            setAtualPage(pg);
            if (!noPagination) window.history.pushState('', '', routeValue);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  });

  const totalPosts = allPosts.length;
  const hasMoreLocalPosts = visibleCount < totalPosts;
  const reachedEnd = isLastPage && !hasMoreLocalPosts;
  const shouldRenderLoadMoreButton = !noScroll && !reachedEnd;

  return (
    <>
      <div id="NewsContainer">
        {dataPosts.length > 0 && <ItemListNews items={dataPosts} page={pageRef.current} backgroundColor={backgroundColor} />}
        <LoadingLogo isLoading={isLoading} />
        {shouldRenderLoadMoreButton && (
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={isLoading || (isLastPage && !hasMoreLocalPosts)}
              className="chip-hover-color [&.chip-hover-color]:hover:[&_path]:fill-mglGray50' rounded-full border-[0.5px] bg-mglGray50 px-6 py-3 font-semibold text-mglGray900 transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 hover:[&.chip-hover-color]:bg-mglGray900 hover:[&.chip-hover-color]:text-mglGray50">
              {isLoading ? 'carregando...' : `ver mais notícias`}
            </button>
          </div>
        )}
      </div>
      {pageNewsSpan && pageNewsSpan.finish()}
    </>
  );
}
